// Native (Capacitor) shell helpers. Everything here is a no-op on the web PWA.
// Plugins are imported once, together, so the first haptic tick isn't a cold load.

export const isNativeApp = () => !!window.Capacitor?.isNativePlatform?.();

let plugins = null;
const load = () => {
  if (!isNativeApp()) return Promise.resolve(null);
  if (plugins) return Promise.resolve(plugins);
  return Promise.all([
    import("@capacitor/status-bar"),
    import("@capacitor/splash-screen"),
    import("@capacitor/keyboard"),
    import("@capacitor/haptics"),
  ])
    .then(([sb, ss, kb, h]) => {
      plugins = {
        StatusBar: sb.StatusBar, Style: sb.Style,
        SplashScreen: ss.SplashScreen,
        Keyboard: kb.Keyboard,
        Haptics: h.Haptics,
      };
      return plugins;
    })
    .catch(() => null);
};

// Status bar + keyboard. Call once on mount.
export const initNativeShell = () => {
  load().then((p) => {
    if (!p) return;
    // targetSdk 36 forces edge-to-edge and ignores the opt-out flag — keep the
    // WebView below the system bars so headings never render under the clock.
    p.StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});
    p.StatusBar.setStyle({ style: p.Style.Dark }).catch(() => {}); // light icons on the teal bar
    if (window.Capacitor?.getPlatform?.() === "android")
      p.StatusBar.setBackgroundColor({ color: "#0f766e" }).catch(() => {});
    p.Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(() => {});
  });
};

// Dismiss the splash. Held open by launchAutoHide:false; index.js arms a 5s
// backstop that runs even if <App/> never mounts.
export const hideSplash = () => {
  load().then((p) => p && p.SplashScreen.hide({ fadeOutDuration: 200 }).catch(() => {}));
};

// Haptic feedback is user-toggleable (Settings → Preferences). Stored in
// localStorage so the check stays synchronous and works from non-React callers.
const HAPTICS_KEY = "ufcm_haptics";
export const hapticsEnabled = () => {
  try { return localStorage.getItem(HAPTICS_KEY) !== "0"; } catch { return true; }
};
export const setHapticsEnabled = (on) => {
  try { localStorage.setItem(HAPTICS_KEY, on ? "1" : "0"); } catch {}
};

// style: "light" | "medium" | "heavy" | "success" | "warning" | "error"
export const haptic = (style = "light") => {
  if (!hapticsEnabled()) return;
  load().then((p) => {
    if (!p) return;
    try {
      if (style === "success" || style === "warning" || style === "error")
        p.Haptics.notification({ type: style.toUpperCase() });
      else p.Haptics.impact({ style: style.toUpperCase() });
    } catch {}
  });
};
